import { Dialog } from "../../ui/layers";
import { useManagedSigningExperienceCopy } from "./managedSigningExperienceCatalog";
import { useTrustAuthorityCopy } from "./trustAuthorityCatalog";
import { useEffect, useMemo, useState } from "react";
import {
  KeyRound,
  Plus,
  RefreshCcw,
  RotateCw,
  ShieldCheck,
} from "lucide-react";

import {
  activateEvidenceWrapIdentity,
  getEvidenceWrapAuthority,
  listEvidenceWrapIdentities,
  stageEvidenceWrapIdentity,
  type EvidenceWrapAuthority,
  type EvidenceWrapIdentity,
} from "./keysTrustApi";
import "../../styles/keys-trust-managed.css";

const STAGE_STORAGE_KEY = "kavoosh.keysTrust.stage.collector_evidence_wrap_key";

function operationId(): string {
  const existing = window.sessionStorage.getItem(STAGE_STORAGE_KEY);
  if (existing) return existing;
  const value = window.crypto.randomUUID();
  window.sessionStorage.setItem(STAGE_STORAGE_KEY, value);
  return value;
}

function shortFingerprint(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-12)}`;
}

export function EvidenceWrapAuthorityPanel({
  onSessionUnavailable,
}: {
  onSessionUnavailable?: () => void;
}) {
  const { t } = useTrustAuthorityCopy();
  const c=useManagedSigningExperienceCopy();
  const [confirmation,setConfirmation]=useState<"generate"|EvidenceWrapIdentity|null>(null);
  const [items, setItems] = useState<EvidenceWrapIdentity[]>([]);
  const [authority, setAuthority] = useState<EvidenceWrapAuthority | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const identities = await listEvidenceWrapIdentities();
      setItems(identities);
      try {
        setAuthority(await getEvidenceWrapAuthority());
      } catch (reason) {
        const detail = reason instanceof Error ? reason.message : "";
        if (detail.includes("unavailable")) setAuthority(null);
        else throw reason;
      }
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("error.loadEvidence");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const staged = useMemo(
    () => items.filter((item) => item.lifecycle === "staged"),
    [items],
  );

  async function generate() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const created = await stageEvidenceWrapIdentity(operationId());
      window.sessionStorage.removeItem(STAGE_STORAGE_KEY);
      setItems((current) => [
        created,
        ...current.filter((item) => item.operation_id !== created.operation_id),
      ]);
      setMessage(
        t("evidence.stagedMessage", { identity: created.key_id ? shortFingerprint(created.key_id) : t("evidence.pendingIdentity") }),
      );
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("error.stageEvidence");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  async function activate(item: EvidenceWrapIdentity) {
    if (item.lifecycle !== "staged") return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const next = await activateEvidenceWrapIdentity(item.operation_id);
      setAuthority(next);
      await load();
      setMessage(
        t("evidence.activatedMessage", { version: String(next.key_version) }),
      );
    } catch (reason) {
      const detail = reason instanceof Error
        ? reason.message
        : t("error.activateEvidence");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  return <section className="keys-trust-managed" aria-labelledby="evidence-wrap-title">
    <header>
      <div>
        <p className="admin-v12-eyebrow">{t("evidence.eyebrow")}</p>
        <h2 id="evidence-wrap-title">{t("evidence.title")}</h2>
        <p>
          {t("evidence.description")}
        </p>
      </div>
      <button type="button" className="admin-v12-secondary" onClick={() => void load()} disabled={loading || busy}>
        <RefreshCcw size={15} />{loading ? t("loading") : t("refresh")}
      </button>
    </header>

    <div className="keys-trust-managed-action">
      <div>
        <strong>{t("purpose")}</strong>
        <p><code>collector_evidence_wrap_key</code> · RSA-4096 OAEP-SHA256</p>
      </div>
      <button type="button" className="admin-v12-primary" onClick={() => setConfirmation("generate")} disabled={busy}>
        <Plus size={16} />{authority ? t("evidence.rotate") : t("evidence.generate")}
      </button>
      <p>
        <ShieldCheck size={15} />
        {t("evidence.safety")}
      </p>
    </div>

    <section className="keys-trust-rotation" aria-label={t("evidence.rotationAria")}>
      <div><RotateCw size={18} /><span><strong>{t("evidence.rotation")}</strong><small>{authority ? t("active") : t("inactive")}</small></span></div>
      <p>
        {t("evidence.rotationHelp")}
      </p>
    </section>

    {error && <p className="admin-v12-form-error" role="alert">{error}</p>}
    {message && <p className="keys-trust-managed-success" role="status">{message}</p>}

    <div className="admin-v12-table-wrap keys-evidence-directory"><table><thead><tr><th>{t("evidence.authority")}</th><th>{t("purpose")}</th><th>{t("fingerprint")}</th><th>{t("provider")}</th><th>{t("evidence.activate")}</th></tr></thead><tbody>{staged.map(item=>{
      const active=item.key_record_id===authority?.key_record_id;
      const retained=item.previously_activated&&!active;
      return <tr key={item.operation_id}><td><strong>{active?t("evidence.activeVersion",{version:String(authority.key_version),fingerprint:shortFingerprint(authority.fingerprint_sha256)}):item.key_id?shortFingerprint(item.key_id):t("pending")}</strong><small>{item.algorithm} · {item.key_size_bits}</small></td><td><b className={`keys-trust-managed-state state-${active?"active":"staged"}`}>{t(active?"evidence.activeCollections":retained?"evidence.retained":"staged")}</b><small>{t(active?"evidence.activeHelp":retained?"evidence.retainedHelp":"evidence.stagedHelp")}</small></td><td><code>{item.fingerprint_sha256?shortFingerprint(item.fingerprint_sha256):t("pending")}</code><details><summary>{t("fingerprint")}</summary><code>{item.fingerprint_sha256}</code><p>{t("privateMaterial")}: {t("neverExposed")}</p></details></td><td><code>{item.provider_kind}</code><small>{item.provider_reference}</small></td><td>{!active&&!retained&&<button type="button" className="admin-v12-secondary" disabled={busy} onClick={()=>setConfirmation(item)}><KeyRound size={14}/>{t("evidence.activate")}</button>}</td></tr>;
    })}</tbody></table>{!loading&&staged.length===0&&<p className="admin-v12-empty">{t("evidence.empty")}</p>}</div>
    <Dialog open={confirmation!==null} onDismiss={()=>setConfirmation(null)} dismissible={!busy} title={t(confirmation==="generate"?"evidence.generate":"evidence.activate")} description={t("evidence.rotationHelp")} footer={<><button type="button" className="admin-v12-secondary" disabled={busy} onClick={()=>setConfirmation(null)}>{c("cancel")}</button><button type="button" className="admin-v12-primary" disabled={busy} onClick={()=>void(async()=>{if(confirmation==="generate")await generate();else if(confirmation)await activate(confirmation);setConfirmation(null);})()}>{t(confirmation==="generate"?"evidence.generate":"evidence.activate")}</button></>}>
      <p>{t("evidence.safety")}</p>{confirmation && confirmation!=="generate" && <code>{confirmation.fingerprint_sha256}</code>}{error && <p role="alert">{error}</p>}
    </Dialog>
  </section>;
}
